import { useEffect, useMemo, useState } from 'react'
import { Check, Copy, WrapText } from 'lucide-react'
import { IconButton } from '../primitives/IconButton'
import styles from './CodeBlock.module.css'

function formatValue(value) {
  if (typeof value === 'string') return value
  if (value === undefined) return ''
  return JSON.stringify(value, null, 2)
}

export function CodeBlock({
  value,
  label = 'Code',
  language = 'text',
  copyable = true,
  defaultWrapped = true,
  className = '',
}) {
  const content = useMemo(() => formatValue(value), [value])
  const [wrapped, setWrapped] = useState(defaultWrapped)
  const [copied, setCopied] = useState(false)

  useEffect(() => setCopied(false), [content])

  const copy = async () => {
    if (!navigator.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <section className={`${styles.block} ${className}`.trim()} aria-label={label}>
      <header className={styles.toolbar}>
        <span className={styles.label}>{label}</span>
        <span className={styles.language}>{language}</span>
        <span className={styles.actions}>
          <IconButton
            variant="ghost"
            className={styles.action}
            label={wrapped ? 'Disable line wrapping' : 'Wrap long lines'}
            title={wrapped ? 'Disable line wrapping' : 'Wrap long lines'}
            aria-pressed={wrapped}
            onClick={() => setWrapped((current) => !current)}
          >
            <WrapText size={15} aria-hidden="true" />
          </IconButton>
          {copyable ? (
            <IconButton
              variant="ghost"
              className={styles.action}
              label={copied ? 'Copied' : `Copy ${label}`}
              title={copied ? 'Copied' : `Copy ${label}`}
              onClick={copy}
            >
              {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
            </IconButton>
          ) : null}
        </span>
      </header>
      <pre className={`${styles.pre} ${wrapped ? styles.wrapped : ''}`.trim()} tabIndex={0} aria-label={`${label} content`}>
        <code>{content}</code>
      </pre>
    </section>
  )
}

export default CodeBlock
